/**
 * AddSectionModal — two-mode modal for adding a section to a template.
 *
 * "Create new"     → creates a fresh empty Section at the current scope
 * "Add existing"   → clones an existing section (with all its question
 *                    memberships) into the target template. Questions are
 *                    referenced by the same FK, so answers propagate into
 *                    this composite document.
 */
import { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import Modal from '../ui/Modal'
import Badge from '../ui/Badge'
import { useUser } from '../../contexts/UserContext'
import { cloneSectionToTemplate, createSection, listVisibleSections } from './api'
import { OWNER_LEVEL_BADGE } from './types'
import type { Scope, Section } from './types'

interface Props {
  isOpen: boolean
  onClose: () => void
  /** The template receiving the section — typically the root of the family. */
  targetTemplateId: string
  scope: Scope
  nextOrder: number
  /** Section IDs already in this template — excluded from the picker. */
  excludeSectionIds?: string[]
  onDone: () => Promise<void>
}

type Mode = 'chooser' | 'create_new' | 'add_existing'

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80)
}

export default function AddSectionModal({
  isOpen,
  onClose,
  targetTemplateId,
  scope,
  nextOrder,
  excludeSectionIds = [],
  onDone,
}: Props) {
  const { user } = useUser()
  const [mode, setMode] = useState<Mode>('chooser')
  const [title, setTitle] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<Section[]>([])
  const [query, setQuery] = useState('')
  const [loadingCandidates, setLoadingCandidates] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setMode('chooser')
      setTitle('')
      setKey('')
      setKeyTouched(false)
      setQuery('')
      setError(null)
      setCandidates([])
    }
  }, [isOpen])

  useEffect(() => {
    if (!keyTouched) setKey(slugify(title))
  }, [title, keyTouched])

  useEffect(() => {
    if (mode !== 'add_existing' || !isOpen) return
    setLoadingCandidates(true)
    listVisibleSections({
      scope_level: scope.level,
      office_id: user?.office?.id || null,
      user_id: user?.id || null,
      program_id: scope.level === 'program' ? (scope.ownerId || null) : null,
    })
      .then(setCandidates)
      .catch((err) => setError(err.message || 'Failed to load sections'))
      .finally(() => setLoadingCandidates(false))
  }, [mode, isOpen, scope, user])

  const filteredCandidates = useMemo(() => {
    const excludeSet = new Set(excludeSectionIds)
    const needle = query.trim().toLowerCase()
    return candidates.filter((s) => {
      if (excludeSet.has(s.id)) return false
      // Don't show sections that already belong to this same template
      if (s.template === targetTemplateId) return false
      if (!needle) return true
      return (
        s.key.toLowerCase().includes(needle) ||
        s.title.toLowerCase().includes(needle) ||
        (s.description || '').toLowerCase().includes(needle)
      )
    })
  }, [candidates, query, excludeSectionIds, targetTemplateId])

  const handleCreateNew = async () => {
    if (!title.trim() || !key.trim()) {
      setError('Title and key are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await createSection({
        template: targetTemplateId,
        key: key.trim(),
        title: title.trim(),
        order: nextOrder,
        owner_level: scope.level,
        owner_office: scope.level === 'office' ? (scope.ownerId || null) : null,
        owner_user: scope.level === 'program_manager' ? (scope.ownerId || null) : null,
        owner_program: scope.level === 'program' ? (scope.ownerId || null) : null,
        parent: null,
        description: '',
        writing_agent_instructions: '',
      })
      await onDone()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to create section')
    } finally {
      setSaving(false)
    }
  }

  const handleCloneExisting = async (s: Section) => {
    setSaving(true)
    setError(null)
    try {
      const body: any = {
        target_template: targetTemplateId,
        owner_level: scope.level,
      }
      if (scope.level === 'office') body.owner_office = scope.ownerId
      if (scope.level === 'program_manager') body.owner_user = scope.ownerId
      if (scope.level === 'program') body.owner_program = scope.ownerId
      await cloneSectionToTemplate(s.id, body)
      await onDone()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to clone section')
    } finally {
      setSaving(false)
    }
  }

  const footer = mode === 'create_new' ? (
    <>
      <button type="button" onClick={() => setMode('chooser')} className="px-4 py-2 text-sm border border-border rounded-md" disabled={saving}>Back</button>
      <button type="button" onClick={handleCreateNew} disabled={saving || !title.trim() || !key.trim()} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50">
        {saving ? 'Creating…' : 'Create section'}
      </button>
    </>
  ) : mode === 'add_existing' ? (
    <button type="button" onClick={() => setMode('chooser')} className="px-4 py-2 text-sm border border-border rounded-md" disabled={saving}>Back</button>
  ) : (
    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-md">Cancel</button>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add section" size="lg" footer={footer}>
      {mode === 'chooser' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add a section to this document. A new section starts empty; cloning an existing one copies its questions
            (same Question rows) so prior answers map through to this document.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button type="button" onClick={() => setMode('create_new')} className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/60">
              <div className="font-medium text-foreground mb-1">Create new</div>
              <div className="text-xs text-muted-foreground">Start an empty section and add questions to it.</div>
            </button>
            <button type="button" onClick={() => setMode('add_existing')} className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/60">
              <div className="font-medium text-foreground mb-1">Add existing</div>
              <div className="text-xs text-muted-foreground">Clone a section from another document — shared Questions carry their answers.</div>
            </button>
          </div>
        </div>
      )}

      {mode === 'create_new' && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Title</span>
            <input autoFocus type="text" className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mission Context" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Key</span>
            <input type="text" className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground font-mono text-sm" value={key} onChange={(e) => { setKey(e.target.value); setKeyTouched(true) }} placeholder="mission_context" />
          </label>
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      )}

      {mode === 'add_existing' && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input autoFocus type="text" className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-foreground" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title, key, or description…" />
          </div>
          {loadingCandidates ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              {candidates.length === 0 ? 'No sections exist at this scope yet.' : 'No matches.'}
            </div>
          ) : (
            <ul className="space-y-2 max-h-96 overflow-auto">
              {filteredCandidates.map((s) => {
                const badge = OWNER_LEVEL_BADGE[s.owner_level]
                return (
                  <li key={s.id}>
                    <button type="button" onClick={() => handleCloneExisting(s)} disabled={saving} className="w-full text-left bg-card border border-border hover:border-primary/60 rounded-lg p-3 disabled:opacity-50">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{s.title}</span>
                        <span className="font-mono text-xs text-muted-foreground">{s.key}</span>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                      {s.description && (
                        <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{s.description}</div>
                      )}
                      <div className="mt-1 text-xs text-muted-foreground">
                        {s.section_questions.length} question{s.section_questions.length === 1 ? '' : 's'}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      )}
    </Modal>
  )
}
